import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Brand";
import {
  LayoutDashboard,
  ScanSearch,
  FishSymbol,
  Bell,
  ShieldHalf,
  Crown,
  Users,
  LogOut,
} from "lucide-react";

const NAV = [
  { to: "/app", end: true, label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/app/scanner", label: "Threat Scanner", icon: ScanSearch, id: "nav-scanner" },
  { to: "/app/phishing", label: "Phishing Checker", icon: FishSymbol, id: "nav-phishing" },
  { to: "/app/alerts", label: "Activity & Alerts", icon: Bell, id: "nav-alerts" },
  { to: "/app/protection", label: "Protection", icon: ShieldHalf, id: "nav-protection" },
  { to: "/app/pricing", label: "Pricing", icon: Crown, id: "nav-pricing" },
];

const ADMIN_NAV = { to: "/app/admin", label: "Admin", icon: Users, id: "nav-admin" };

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Sidebar */}
      <aside className="w-16 md:w-64 shrink-0 border-r border-border bg-card flex flex-col sticky top-0 h-screen">
        <div className="h-16 flex items-center px-3 md:px-5 border-b border-border">
          <div className="hidden md:block"><Logo /></div>
          <div className="md:hidden mx-auto"><Logo withText={false} size={22} /></div>
        </div>

        <nav className="flex-1 py-4 space-y-1 px-2 md:px-3">
          {[...NAV, ...(user?.role === "admin" ? [ADMIN_NAV] : [])].map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.id}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2.5 font-mono text-sm border transition-[color,border-color,background-color] duration-200 ${
                    isActive
                      ? "bg-primary/10 border-primary/50 text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                  }`
                }
              >
                <Icon size={18} strokeWidth={1.5} className="shrink-0" />
                <span className="hidden md:inline">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-border p-2 md:p-3">
          <div className="hidden md:flex items-center gap-2 px-2 py-2 mb-1">
            <div className="w-8 h-8 bg-primary/10 border border-primary/40 flex items-center justify-center font-mono text-primary text-sm">
              {(user?.name || "U").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm truncate">{user?.name}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-primary">
                {user?.role}
              </div>
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 font-mono text-sm text-muted-foreground hover:text-destructive border border-transparent hover:border-destructive/40 transition-colors duration-200"
          >
            <LogOut size={18} strokeWidth={1.5} />
            <span className="hidden md:inline">Sign out</span>
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
